import { useTranslation } from 'react-i18next';

import { ExportType } from 'cli/enums';
import { ICliJob } from 'cli/types';
import { ApiStatus, NotificationPosition } from 'core/enums';
import { useNotificationContext } from 'core/states';
import { getDomainUrl } from 'core/utils';

interface IGenerateFileOptions {
  exportSuccessMessage?: string;
  onGenerateExportFinished?: (cliJob: ICliJob) => void;
}

export const useGenerateExport = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotificationContext();

  const downloadFilePath = (fileId: string) =>
    `${getDomainUrl()}/files/index/download/file/${fileId}`;

  const downloadArchivePath = (archiveName: string) =>
    `${getDomainUrl()}/part_v2/media/download-archive/archive/${archiveName}`;

  const onGenerateExportFinished = (
    exportType: ExportType,
    cliJob: ICliJob,
    options?: IGenerateFileOptions
  ) => {
    const defaultOptions = {
      exportSuccessMessage: t('messageGenerateExportSuccess'),
      ...options,
    };

    showNotification({
      type: ApiStatus.SUCCESS,
      title: t('titleSuccess'),
      message: defaultOptions.exportSuccessMessage,
      position: NotificationPosition.TOP,
    });

    const { fileId, reportFileId, archiveName, zipFileId } = cliJob.result as Record<
      string,
      string
    >;

    switch (exportType) {
      case ExportType.FILE:
        // zipFileId (SKU export's CSV + Trans Mfr Part Nbr bundle) is download-only and present
        // only when a qualifying T-Item exists; other exports never emit it, so this is inert.
        window.location.href = downloadFilePath(zipFileId || fileId || reportFileId);

        break;
      case ExportType.ARCHIVE:
        window.location.href = downloadArchivePath(archiveName);

        break;
      default:
        showNotification({
          type: ApiStatus.ERROR,
          title: t('titleError'),
          message: t('errorUnknownExportType'),
          position: NotificationPosition.TOP,
        });

        break;
    }

    options?.onGenerateExportFinished?.(cliJob);
  };

  const onGenerateExportStopped = (cliJob: ICliJob) => {
    showNotification({
      type: ApiStatus.ERROR,
      title: t('titleError'),
      message: cliJob.statusDescription || t('serverError'),
      position: NotificationPosition.TOP,
    });
  };

  return {
    onGenerateExportFinished,
    onGenerateExportStopped,
    downloadFilePath,
    downloadArchivePath,
  };
};
